import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AppConfig } from 'src/app/app.config';
import { Observable, map } from 'rxjs';

import L from 'leaflet';


export class TaskSubLocation {
    Id: string;
    TaskId: string;
    Location: any;
    SystemGenerated: boolean;
}

export class WorksiteSubLocation {
    Id: string;
    WorksiteId: string;
    Location: any;
}

export interface County {
    Code: string;
    Name: string;
    CountryCode: string;
}

@Injectable()
export class MapService {

    constructor(
        private http: HttpClient,
        private config: AppConfig
    ) {
    }

    public getCustomerLayers(): Observable<any> {
        return this.http.get(`${this.config.backendMapUri}CommonMapWebAPI/api/Wfs/GetCustomerLayers`);
    }

    public getFeaturesWithMetadataInLocation(lat: number, lon: number): Observable<any> {
        return this.http.get(`${this.config.backendMapUri}CommonMapWebAPI/api/Wfs/GetFeaturesWithMetadataInLocation?point.latitude=${lat}&point.longitude=${lon}`);
    }

    public getFeaturesWithMetadataInBoundingBox(minLat: number, minLon: number, maxLat: number, maxLon: number): Observable<any> {
        return this.http.get(`${this.config.backendMapUri}CommonMapWebAPI/api/Wfs/GetFeaturesWithMetadataInBoundingBox?bbox.minPoint.latitude=${minLat}&bbox.minPoint.longitude=${minLon}&bbox.maxPoint.latitude=${maxLat}&bbox.maxPoint.longitude=${maxLon}`);
    }

    public getExternalLayers(): Observable<any> {
        return this.http.get(`${this.config.backendMapUri}CommonMapWebAPI/api/Wfs/GetExternalCustomerLayers`);
    }

    public getTaskSubLocations(taskId: string): Observable<TaskSubLocation[]> {
        return this.http.get<TaskSubLocation[]>(`${this.config.backendIwmUri}Api/api/TaskSubLocations/Get?targetTaskId=${taskId}`);
    }

    public getWorksiteSubLocations(worksiteId: string): Observable<WorksiteSubLocation[]> {
        return this.http.get<WorksiteSubLocation[]>(`${this.config.backendIwmUri}WorksiteApi/api/WorksiteSubLocations/Get?targetWorksiteId=${worksiteId}`);
    }

    public addTaskSubLocation(subLocation: TaskSubLocation): Observable<TaskSubLocation> {
        return this.http.post<TaskSubLocation>(`${this.config.backendIwmUri}Api/api/TaskSubLocations/Post`, subLocation);
    }

    public addWorksiteSubLocation(subLocation: WorksiteSubLocation): Observable<WorksiteSubLocation> {
        return this.http.post<WorksiteSubLocation>(`${this.config.backendIwmUri}WorksiteApi/api/WorksiteSubLocations/Post`, subLocation);
    }

    public updateTaskSubLocation(subLocation: TaskSubLocation): Observable<TaskSubLocation> {
        return this.http.put<TaskSubLocation>(`${this.config.backendIwmUri}Api/api/TaskSubLocations/Put`, subLocation);
    }

    public updateWorksiteSubLocation(subLocation: WorksiteSubLocation): Observable<WorksiteSubLocation> {
        return this.http.put<WorksiteSubLocation>(`${this.config.backendIwmUri}WorksiteApi/api/WorksiteSubLocations/Put`, subLocation);
    }

    public removeTaskSubLocation(id: string): Observable<any> {
        return this.http.delete(`${this.config.backendIwmUri}Api/api/TaskSubLocations?targetSubLocationId=${id}`);
    }

    public removeWorksiteSubLocation(id: string): Observable<any> {
        return this.http.delete(`${this.config.backendIwmUri}WorksiteApi/api/WorksiteSubLocations?targetSubLocationId=${id}`);
    }

    public getCounties(countryCode: string): Observable<County[]> {
        return this.http.get(`${this.config.backendMapUri}CommonMapWebAPI/Municipalities?$filter=CountryCode eq '${countryCode}'`)
            .pipe(map((response: Response) => {
                return response['value'];
            }));
    }

    public createTaskFromObservation(observationId: string): any {
        return this.http.post<any>(`${this.config.backendIwmUri}Api/api/TaskObservations/CreateTask?observationId=${observationId}`, null);
    }

    public getObservations(): Observable<GeoJSON.Feature[]> {
        return this.http.get<GeoJSON.Feature[]>(`${this.config.backendIwmUri}Api/api/TaskObservations/GetObservations`);
    }

    public getIcon(iconName: string): L.Icon<L.IconOptions> {

        const existingIcons = ['blue', 'gray', 'green', 'red', 'white'];
        if (existingIcons.find(o => o === iconName) !== undefined) {
            return this.getColoredIcon(iconName);
        }

        return L.icon({
            iconRetinaUrl: './assets/leaflet/marker-icon-2x.png',
            iconUrl: './assets/leaflet/marker-icon.png',
            shadowUrl: './assets/leaflet/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            tooltipAnchor: [16, -28],
            shadowSize: [41, 41]
        });
    }

    private getColoredIcon(iconName: string): L.Icon<L.IconOptions> {
        return L.icon({
            iconRetinaUrl: `./assets/mapmarkers/marker-icon-2x-${iconName}.png`,
            iconUrl: `./assets/mapmarkers/marker-icon-${iconName}.png`,
            shadowUrl: './assets/mapmarkers/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            tooltipAnchor: [16, -28],
            shadowSize: [41, 41]
        });
    }
}
